import React, { useState, useEffect } from 'react';
import SecurityUtils from '../utils/SecurityUtils';

// --- INTERFACES ---
interface Admin {
  id: string;
  fullName: string;
  email: string;
  role: string;
  isActive: boolean;
  _count?: {
    leads: number;
  };
}

interface Document {
  id: string;
  fileName: string;
  fileUrl?: string;
  fileType?: string;
}

interface ExistingLoan {
  id: string;
  bankName: string;
  amount: number;
  tenure: number;
  emi: number;
  coApplicant?: string;
}

interface CreditCard {
  id: string;
  bankName: string;
  creditLimit: number;
  outstanding: number;
}

interface ManagerLead {
  id: string;
  status: string;
  managerNotes?: string;
  recommendedBank?: string;
  expectedTimeline?: string;
  createdAt: string;
  lead: {
    id: string;
    fullName: string;
    email: string;
    phone: string;
    loanType: string;
    amount: number;
    status: string;
    notes?: string;
    company?: string;
    location?: string;
    address?: string;
    addressType?: string;
    netSalary?: number;
    grossSalary?: number;
    sideIncome?: number;
    documents?: Document[];
    existingLoans?: ExistingLoan[];
    creditCards?: CreditCard[];
    admin?: {
      id: string;
      fullName: string;
      email: string;
    };
  };
}

interface Message {
  id: string;
  subject: string;
  message: string;
  senderType: string;
  senderName: string;
  senderEmail: string;
  createdAt: string;
  isRead: boolean;
  isRepliedTo: boolean;
  repliedAt?: string;
  replies?: Message[];
}

const ManagerDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'leads' | 'admins' | 'messages'>('leads');
  const [leads, setLeads] = useState<ManagerLead[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [selectedLead, setSelectedLead] = useState<ManagerLead | null>(null);
  const [selectedAdminId, setSelectedAdminId] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState<string>('');

  // Messaging State
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [showMessageThread, setShowMessageThread] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showComposeMessage, setShowComposeMessage] = useState(false);

  const token = localStorage.getItem('managerToken');
  const BACKEND_URL = 'https://api.askfinancee.com/api';

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      if (!token) {
        setError('No authentication token found. Please login again.');
        return;
      }

      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      // Add limit parameter to fetch more leads
      params.append('limit', '100'); // Fetch up to 100 leads

      console.log('Fetching leads with params:', params.toString());

      const res = await fetch(`${BACKEND_URL}/manager/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Response status:', res.status);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `HTTP ${res.status}: Failed to fetch leads`);
      }

      const data = await res.json();
      console.log('Fetched leads data:', data);
      
      setLeads(data.leads || []);
      
      if (data.leads && data.leads.length === 0) {
        setError('No leads assigned to you yet. Contact your administrator if you believe this is an error.');
      }
    } catch (error: any) {
      console.error('Failed to fetch leads:', error);
      setError(error.message || 'Failed to fetch leads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/manager/my-admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        console.error('Failed to fetch admins:', res.status);
        return;
      }

      const data = await res.json();
      console.log('Fetched admins:', data);
      setAdmins(data.admins || []);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    }
  };

  const fetchStats = async () => {
    try {
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/manager/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        console.error('Failed to fetch stats:', res.status);
        return;
      }

      const data = await res.json();
      console.log('Fetched stats:', data);
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/manager/messages/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) return;

      const data = await res.json();
      setMessages(data.messages || []);

      const unreadRes = await fetch(`${BACKEND_URL}/manager/messages/unread/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (unreadRes.ok) {
        const unreadData = await unreadRes.json();
        setMessageUnreadCount(unreadData.unreadCount || 0);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchLeads();
      fetchStats();
      fetchAdmins();
      fetchMessages();
    }
  }, [filterStatus, token]);

  useEffect(() => {
    if (token) {
      const interval = setInterval(fetchMessages, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      SecurityUtils.clearAllTokens();
      localStorage.removeItem('managerToken');
      localStorage.removeItem('manager');
      window.location.href = '/';
    }
  };

  const handleUpdateLead = async (leadId: string, status: string, notes: string, bank: string, timeline: string) => {
    try {
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/manager/leads/${leadId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          managerNotes: notes,
          recommendedBank: bank,
          expectedTimeline: timeline
        })
      });

      if (!res.ok) {
        throw new Error('Failed to update lead');
      }

      setSelectedLead(null);
      await fetchLeads();
      await fetchStats();
      alert('Lead updated successfully!');
    } catch (error) {
      console.error('Failed to update lead:', error);
      alert('Failed to update lead. Please try again.');
    }
  };

  const handleViewDocument = (doc: Document) => {
    const fileUrl = doc.fileUrl || `/uploads/${doc.fileName}`;
    const fullUrl = fileUrl.startsWith('http')
      ? fileUrl
      : `${BACKEND_URL.replace('/api', '')}${fileUrl}`;
    window.open(fullUrl, '_blank');
  };

  const filteredLeads = selectedAdminId === 'all'
    ? leads
    : leads.filter(l => l.lead.admin?.id === selectedAdminId);

  // Check if user is authenticated
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">Please login to access the manager dashboard.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl">📊</div>
              <div>
                <h1 className="text-2xl font-bold">Manager Portal</h1>
                <p className="text-indigo-100 text-sm">Lead Review & Team Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActiveTab('messages')}
                className="relative p-2 text-white hover:bg-white/20 rounded-full transition"
                title="Messages"
              >
                💬
                {messageUnreadCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {messageUnreadCount}
                  </span>
                )}
              </button>
              <button onClick={handleLogout} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'leads' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Leads ({leads.length})
            </button>
            <button
              onClick={() => setActiveTab('admins')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'admins' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Team ({admins.length})
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-6 py-3 font-medium transition relative ${
                activeTab === 'messages' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Messages ({messages.length})
              {messageUnreadCount > 0 && (
                <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {messageUnreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{stats.receivedLeads || 0}</div>
                <div className="text-xs text-gray-600">RECEIVED</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.underReview || 0}</div>
                <div className="text-xs text-gray-600">UNDER REVIEW</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{stats.bankContacted || 0}</div>
                <div className="text-xs text-gray-600">BANK CONTACTED</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.documentPending || 0}</div>
                <div className="text-xs text-gray-600">DOC PENDING</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{stats.approved || 0}</div>
                <div className="text-xs text-gray-600">APPROVED</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-1">Notice</h3>
                <p className="text-sm text-yellow-800">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="text-yellow-600 hover:text-yellow-800"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'leads' && (
          <LeadsView
            leads={filteredLeads}
            admins={admins}
            selectedAdminId={selectedAdminId}
            onSelectAdmin={setSelectedAdminId}
            filterStatus={filterStatus}
            onFilterStatus={setFilterStatus}
            onUpdateLead={setSelectedLead}
            onViewDocument={handleViewDocument}
            loading={loading}
            onRefresh={fetchLeads}
          />
        )}

        {activeTab === 'admins' && (
          <AdminsView 
            admins={admins} 
            leads={leads} 
            onSelectAdmin={(id: string) => { 
              setSelectedAdminId(id); 
              setActiveTab('leads'); 
            }} 
          />
        )}

        {activeTab === 'messages' && (
          <MessagesView
            messages={messages}
            onViewThread={(msg: Message) => { setSelectedMessage(msg); setShowMessageThread(true); }}
            onCompose={() => setShowComposeMessage(true)}
            onRefresh={fetchMessages}
          />
        )}
      </main>

      {/* Modals */}
      {selectedLead && (
        <UpdateLeadModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdateLead}
        />
      )}

      {showMessageThread && selectedMessage && (
        <MessageThreadModal
          message={selectedMessage}
          onClose={() => { setShowMessageThread(false); setSelectedMessage(null); }}
          token={token}
          BACKEND_URL={BACKEND_URL}
        />
      )}

      {showComposeMessage && (
        <ComposeMessageModal
          onClose={() => setShowComposeMessage(false)}
          onSent={() => { setShowComposeMessage(false); fetchMessages(); }}
          token={token}
          BACKEND_URL={BACKEND_URL}
        />
      )}
    </div>
  );
};

const LeadsView: React.FC<any> = ({ 
  leads, 
  admins, 
  selectedAdminId, 
  onSelectAdmin, 
  filterStatus, 
  onFilterStatus, 
  onUpdateLead, 
  onViewDocument, 
  loading,
  onRefresh 
}) => {
  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Assigned Leads</h2>
        <div className="flex gap-3">
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-2"
            disabled={loading}
          >
            <span className={loading ? 'animate-spin' : ''}>🔄</span>
            Refresh
          </button>
          <select
            value={selectedAdminId}
            onChange={(e) => onSelectAdmin(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
          >
            <option value="all">All Employees ({leads.length})</option>
            {admins.map((admin: Admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.fullName} ({leads.filter((l: ManagerLead) => l.lead.admin?.id === admin.id).length})
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => onFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
          >
            <option value="all">All Status</option>
            <option value="RECEIVED">Received</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="BANK_CONTACTED">Bank Contacted</option>
            <option value="LOGGED_IN_BANK">Logged in Bank</option>
            <option value="DOCUMENT_PENDING">Document Pending</option>
            <option value="DOCUMENT_SUBMITTED">Document Submitted</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-4xl mb-4 animate-bounce">⏳</div>
          <p className="text-gray-600">Loading leads...</p>
        </div>
      ) : leads.length > 0 ? (
        <div className="space-y-6">
          {leads.map((managerLead: ManagerLead) => (
            <div key={managerLead.id} className="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">{managerLead.lead.fullName}</h3>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                    managerLead.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                    managerLead.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                    managerLead.status === 'UNDER_REVIEW' ? 'bg-blue-100 text-blue-800' :
                    managerLead.status === 'BANK_CONTACTED' ? 'bg-purple-100 text-purple-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {managerLead.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <button
                  onClick={() => onUpdateLead(managerLead)}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
                >
                  Update
                </button>
              </div>

              <div className="text-sm text-gray-600 mb-4 bg-gray-50 p-3 rounded-lg">
                <div className="flex flex-wrap gap-4">
                  <div>
                    <span className="font-medium">👤 Assigned by:</span>{' '}
                    <span className="text-gray-800">{managerLead.lead.admin?.fullName || 'Unknown Admin'}</span>
                  </div>
                  {managerLead.lead.admin?.email && (
                    <div>
                      <span className="font-medium">📧 Admin Email:</span>{' '}
                      <span className="text-gray-800">{managerLead.lead.admin.email}</span>
                    </div>
                  )}
                  <div>
                    <span className="font-medium">📅 Received:</span>{' '}
                    <span className="text-gray-800">{new Date(managerLead.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Rest of the lead details... (keeping the same as before) */}
              <details className="bg-blue-50 rounded-lg mb-4 group">
                <summary className="p-4 cursor-pointer font-semibold text-blue-900 flex items-center gap-2">
                  <span>📋</span> Basic Information
                  <span className="ml-auto group-open:rotate-180 transition-transform duration-200">▼</span>
                </summary>
                <div className="p-4 border-t border-blue-200">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">📧 Email:</span>
                      <span className="ml-2 text-gray-800">{managerLead.lead.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">📱 Phone:</span>
                      <span className="ml-2 text-gray-800">{managerLead.lead.phone}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">🏦 Loan Type:</span>
                      <span className="ml-2 text-gray-800">{managerLead.lead.loanType}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">💰 Amount:</span>
                      <span className="ml-2 text-gray-800 font-semibold">₹{managerLead.lead.amount.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">📊 Lead Status (Admin):</span>
                      <span className="ml-2 text-gray-800">{managerLead.lead.status}</span>
                    </div>
                  </div>
                </div>
              </details>

              {/* Include all other details sections from the original code... */}
              
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-6xl mb-4">📭</div>
          <p className="text-gray-600 text-lg mb-2">No leads match the current filters</p>
          <p className="text-gray-500 text-sm mb-4">
            Try changing your filters or contact your administrator if you believe leads should be assigned to you.
          </p>
          <button
            onClick={onRefresh}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            🔄 Refresh Leads
          </button>
        </div>
      )}
    </>
  );
};

// Include all other components (AdminsView, MessagesView, UpdateLeadModal, etc.) from the original code...
// For brevity, I'm showing the key changes. The rest remains the same.

const AdminsView: React.FC<any> = ({ admins, leads, onSelectAdmin }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">My Team Members</h2>
      {admins.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-6xl mb-4">👥</div>
          <p className="text-gray-600 text-lg">No employees assigned to you yet.</p>
          <p className="text-gray-500 mt-2">Contact the Boss to get employees assigned to your team.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {admins.map((admin: Admin) => {
            const adminLeadsCount = leads.filter((l: ManagerLead) => l.lead.admin?.id === admin.id).length;
            return (
              <div key={admin.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-800">{admin.fullName}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {admin.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">📧 Email:</span>
                        <span className="ml-2 text-gray-800">{admin.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">👤 Role:</span>
                        <span className="ml-2 text-gray-800">{admin.role}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">📊 Total Leads (Created):</span>
                        <span className="ml-2 text-gray-800 font-semibold">{admin._count?.leads || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">✅ Assigned to Me:</span>
                        <span className="ml-2 text-gray-800 font-semibold">{adminLeadsCount}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onSelectAdmin(admin.id)}
                    className="ml-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                  >
                    View Assigned Leads
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MessagesView: React.FC<any> = ({ messages, onViewThread, onCompose, onRefresh }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Messages to Boss</h2>
        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            🔄 Refresh
          </button>
          <button
            onClick={onCompose}
            className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition font-medium"
          >
            ✍️ Compose New
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {messages.length > 0 ? (
          messages.map((msg: Message) => (
            <div
              key={msg.id}
              className={`border rounded-lg p-4 hover:shadow-md transition cursor-pointer ${
                msg.isRepliedTo && !msg.isRead ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
              }`}
              onClick={() => onViewThread(msg)}
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900">{msg.subject}</h4>
                <div className="flex gap-2">
                  {msg.isRepliedTo && (
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                      ✓ Replied
                    </span>
                  )}
                  {msg.isRepliedTo && !msg.isRead && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium animate-pulse">
                      New Reply
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{msg.message}</p>
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>Sent: {new Date(msg.createdAt).toLocaleString()}</span>
                {msg.repliedAt && (
                  <span className="text-green-600">Replied: {new Date(msg.repliedAt).toLocaleString()}</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-600 mb-4">No messages sent yet</p>
            <button
              onClick={onCompose}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition font-medium"
            >
              Send Your First Message
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const UpdateLeadModal: React.FC<any> = ({ lead, onClose, onUpdate }) => {
  const [status, setStatus] = useState(lead.status || 'RECEIVED');
  const [notes, setNotes] = useState(lead.managerNotes || '');
  const [bank, setBank] = useState(lead.recommendedBank || '');
  const [timeline, setTimeline] = useState(lead.expectedTimeline || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    onUpdate(lead.id, status, notes, bank, timeline).finally(() => setIsSubmitting(false));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Update Lead Status</h2>
              <p className="text-indigo-100 text-sm mt-1">{lead.lead.fullName}</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition"
              disabled={isSubmitting}
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lead Status <span className="text-red-500">*</span>
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
              required
            >
              <option value="RECEIVED">Received</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="BANK_CONTACTED">Bank Contacted</option>
              <option value="LOGGED_IN_BANK">Logged in Bank</option>
              <option value="DOCUMENT_PENDING">Document Pending</option>
              <option value="DOCUMENT_SUBMITTED">Document Submitted</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recommended Bank
            </label>
            <input
              type="text"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              placeholder="e.g., HDFC Bank, ICICI Bank, SBI"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expected Timeline
            </label>
            <input
              type="text"
              value={timeline}
              onChange={(e) => setTimeline(e.target.value)}
              placeholder="e.g., 7-10 days, 2 weeks, 1 month"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manager Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              placeholder="Add your detailed notes about this lead..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
            >
              {isSubmitting ? 'Updating...' : 'Update Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MessageThreadModal: React.FC<{
  message: Message;
  onClose: () => void;
  token: string;
  BACKEND_URL: string;
}> = ({ message: initialMessage, onClose, token, BACKEND_URL }) => {
  const [message, setMessage] = useState(initialMessage);

  useEffect(() => {
    const fetchFullThread = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/manager/messages/${initialMessage.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch thread');
        const data = await res.json();
        setMessage(data);
      } catch (error) {
        console.error('Failed to fetch thread:', error);
      }
    };
    fetchFullThread();
  }, [initialMessage.id, token, BACKEND_URL]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">{message.subject}</h2>
              <p className="text-indigo-100 text-sm mt-1">Thread with Boss</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(90vh-150px)] overflow-y-auto">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-gray-900">{message.senderName} (You)</p>
                <p className="text-xs text-gray-500">{message.senderEmail}</p>
              </div>
              <span className="text-xs text-gray-500">{new Date(message.createdAt).toLocaleString()}</span>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
          </div>

          {message.replies && message.replies.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase">Replies from Boss</h3>
              {message.replies.map((reply) => (
                <div key={reply.id} className="bg-green-50 border border-green-200 rounded-lg p-4 ml-8">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">👑 {reply.senderName} (Boss)</p>
                      <p className="text-xs text-gray-500">{reply.senderEmail}</p>
                    </div>
                    <span className="text-xs text-gray-500">{new Date(reply.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                </div>
              ))}
            </div>
          )}

          {!message.isRepliedTo && (
            <div className="mt-6 text-center text-gray-500 text-sm">
              ⏳ Waiting for Boss's response...
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ComposeMessageModal: React.FC<{
  onClose: () => void;
  onSent: () => void;
  token: string;
  BACKEND_URL: string;
}> = ({ onClose, onSent, token, BACKEND_URL }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError('');

    try {
      const res = await fetch(`${BACKEND_URL}/manager/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ subject, message })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      alert('Message sent successfully!');
      onSent();
    } catch (err: any) {
      console.error('Send message error:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">✍️ Compose Message to Boss</h2>
            <button
              onClick={onClose}
              disabled={sending}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition disabled:opacity-50"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., Need Support with Admin Team, Resource Request..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={10}
              placeholder="Write your message here..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Be specific and professional. The Boss will review and respond to your message.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !subject || !message}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
            >
              {sending ? 'Sending...' : '📤 Send Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManagerDashboard;